import { useContext } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — renders a human-readable date from a raw value.
 *
 * Accepts the shapes the Elasticsearch envelope produces for date fields :
 *   - a Unix timestamp in **seconds** (what Search API indexes `created` /
 *     `changed` as, e.g. `1781107021`),
 *   - a Unix timestamp in milliseconds,
 *   - an ISO-8601 string.
 *
 * Heuristic : a finite number below 1e12 is treated as seconds and scaled to
 * milliseconds; anything else is handed to `new Date()` as-is.
 *
 * YAML usage (inside a content array):
 *   - type: FormatDate
 *     value: ~.created
 *     locale: fr-FR              # optional, default 'fr-FR'
 *     dateStyle: short           # optional Intl dateStyle (default 'medium')
 *     timeStyle: short           # optional Intl timeStyle (omit = date only)
 *     empty: '—'                 # optional fallback when value is empty/invalid
 */
export const FormatDate = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const raw = resolve(props.value)
  const locale = resolve(props.locale) || 'fr-FR'
  const dateStyle = resolve(props.dateStyle) || 'medium'
  const timeStyle = resolve(props.timeStyle) || undefined
  const empty = resolve(props.empty) ?? ''

  if (raw == null || raw === '') return <>{empty}</>

  let date
  if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
    const n = Number(raw)
    date = new Date(n < 1e12 ? n * 1000 : n)
  } else {
    date = new Date(raw)
  }
  if (Number.isNaN(date.getTime())) return <>{empty}</>

  const opts = timeStyle ? { dateStyle, timeStyle } : { dateStyle }
  return <>{new Intl.DateTimeFormat(locale, opts).format(date)}</>
}
